import React from "react";
import { DateFormat, formatDate, getNumberFormat } from "../../../utils";
import {
  isPricedValuation,
  valuationAmountText,
  valuationMessageFor,
} from "../../../services/valuation";
import { AccountCardProps, SparklineChartProps } from "./types";
import {
  AccountHeaderRow,
  AccountCardSurface,
  AccountOpenButton,
  AccountTitle,
  InactiveBadge,
  InactiveDot,
  InactivityBanner,
  InactivityIcon,
  NoHistoryIcon,
  BalanceSection,
  BalanceLabel,
  BalanceAmount,
  SparklineWrapper,
  SparklineMeta,
  SparklineMetaLabel,
  SparklineDelta,
  NoHistoryNote,
  MetricsGrid,
  MetricBox,
  MetricLabel,
  MetricValue,
  MobileDelta,
  CompactMeta,
  CardContentWrapper,
} from "./styles";

const SparklineChart: React.FC<SparklineChartProps> = ({
  data,
  positive,
  accountId,
}) => {
  const W = 280;
  const H = 60;
  const pad = 3;

  const min = Math.min(...data);
  const max = Math.max(...data);
  const range = max - min || 1;

  const pts = data.map(
    (v, i) =>
      [
        pad + (i / (data.length - 1)) * (W - 2 * pad),
        H - pad - ((v - min) / range) * (H - 2 * pad),
      ] as [number, number],
  );

  const linePath = pts
    .map(([x, y], i) => `${i === 0 ? "M" : "L"}${x.toFixed(2)},${y.toFixed(2)}`)
    .join(" ");
  const areaPath = `${linePath} L${pts.at(-1)![0].toFixed(2)},${H} L${pts[0][0].toFixed(2)},${H} Z`;

  const [lastX, lastY] = pts.at(-1)!;
  const gradId = `spark-${accountId}`;
  const stroke = positive ? "var(--color-success)" : "var(--color-error)";

  return (
    <svg
      viewBox={`0 0 ${W} ${H}`}
      width="100%"
      height="100%"
      preserveAspectRatio="none"
      aria-hidden="true"
    >
      <defs>
        <linearGradient id={gradId} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={stroke} stopOpacity="0.22" />
          <stop offset="100%" stopColor={stroke} stopOpacity="0" />
        </linearGradient>
      </defs>
      <path d={areaPath} fill={`url(#${gradId})`} />
      <path
        d={linePath}
        fill="none"
        stroke={stroke}
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <circle cx={lastX} cy={lastY} r="3.5" fill={stroke} />
    </svg>
  );
};

const SparklineChartMemo = React.memo(SparklineChart);

const formatDelta = (delta: number): string => {
  const abs = getNumberFormat(Math.abs(delta));
  return delta >= 0 ? `+${abs}€` : `-${abs}€`;
};

const AccountCardComponent: React.FC<AccountCardProps> = ({
  account,
  handleOnClick,
}) => {
  const {
    id,
    name,
    balance,
    startDate,
    lastTransactionDate,
    transactionsCount,
    isActive,
  } = account;

  const history = account.balanceHistory ?? [];
  // The nullness half is redundant with `isPricedValuation` and kept because it
  // is what narrows `balance` for the arithmetic below; the helper is what stops
  // a ledger account reading "Unavailable" here and "—" on the Investments page
  // while the same quote is still in flight.
  const hasCurrentBalance =
    balance !== null &&
    isPricedValuation({ value: balance, valuation: account.valuation });
  const hasHistory = hasCurrentBalance && history.length > 1;
  const delta = hasHistory ? balance - history[0] : 0;
  const isDeltaPositive = delta >= 0;
  const isPositive = balance === null || balance >= 0;
  // One message drives the visible balance label and the amount's accessible name.
  const valuationMessage = valuationMessageFor(account.valuation);
  const knownBalanceLabel = isActive ? "Current Balance" : "Last Known Balance";
  const balanceLabel = hasCurrentBalance ? knownBalanceLabel : valuationMessage;
  const balanceAriaLabel = hasCurrentBalance
    ? `${balanceLabel}: ${getNumberFormat(balance)} euros`
    : balanceLabel;
  const balanceText = valuationAmountText({
    value: balance,
    valuation: account.valuation,
    format: (value) => `${getNumberFormat(value)}€`,
  });
  // Only show a trend when we have real balance history to back it up.
  // The "30 days" / "30d" labels reflect the backend balanceHistory window
  // (accountsService: moment().subtract(30, "days")), which returns 31
  // inclusive daily points. Note: for a past-year filter the window is the
  // last 30 days *of that selected year* (ending Dec 31), not trailing from
  // today, so the static label is approximate when viewing prior years.
  const showTrend = isActive && hasHistory;
  const showNoHistoryNote = isActive && hasCurrentBalance && !hasHistory;

  return (
    <AccountCardSurface $cursor={true}>
      <CardContentWrapper>
        <AccountHeaderRow>
          <AccountTitle>
            <AccountOpenButton
              type="button"
              onClick={() => handleOnClick(account)}
            >
              <span>{name}</span>
            </AccountOpenButton>
          </AccountTitle>
          {!isActive && (
            <InactiveBadge>
              <InactiveDot />
              Inactive
            </InactiveBadge>
          )}
          {showTrend && (
            <MobileDelta $positive={isDeltaPositive}>
              {formatDelta(delta)} · 30d
            </MobileDelta>
          )}
        </AccountHeaderRow>

        <BalanceSection>
          <BalanceLabel>{balanceLabel}</BalanceLabel>
          <BalanceAmount
            $positive={isPositive}
            $inactive={!isActive}
            aria-label={balanceAriaLabel}
          >
            {balanceText}
          </BalanceAmount>
        </BalanceSection>

        {showTrend && (
          <>
            <SparklineWrapper>
              <SparklineChartMemo
                data={history}
                positive={isPositive}
                accountId={id}
              />
            </SparklineWrapper>
            <SparklineMeta>
              <SparklineMetaLabel>30 days</SparklineMetaLabel>
              <SparklineDelta $positive={isDeltaPositive}>
                {formatDelta(delta)}
              </SparklineDelta>
            </SparklineMeta>
          </>
        )}

        {showNoHistoryNote && (
          <NoHistoryNote>
            <NoHistoryIcon />
            <span>No balance history yet</span>
          </NoHistoryNote>
        )}

        {!isActive && (
          <InactivityBanner>
            <InactivityIcon />
            {lastTransactionDate ? (
              <strong>
                Last activity ·{" "}
                {formatDate(lastTransactionDate, DateFormat.DMY_SPACE)}
              </strong>
            ) : (
              <span>No activity recorded</span>
            )}
          </InactivityBanner>
        )}

        <MetricsGrid>
          <MetricBox>
            <MetricLabel>Transactions</MetricLabel>
            <MetricValue>{transactionsCount}</MetricValue>
          </MetricBox>
          <MetricBox>
            <MetricLabel>Since</MetricLabel>
            <MetricValue>
              {formatDate(startDate, DateFormat.MY_SPACE)}
            </MetricValue>
          </MetricBox>
          {isActive && (
            <MetricBox>
              <MetricLabel>Last</MetricLabel>
              <MetricValue>
                {lastTransactionDate
                  ? formatDate(lastTransactionDate, DateFormat.DMY_SPACE)
                  : "No Activity"}
              </MetricValue>
            </MetricBox>
          )}
        </MetricsGrid>

        <CompactMeta>
          since {formatDate(startDate, DateFormat.MY_SPACE)} ·{" "}
          {transactionsCount} tx
          {lastTransactionDate
            ? ` · last ${formatDate(lastTransactionDate, DateFormat.MY_SPACE)}`
            : ""}
        </CompactMeta>
      </CardContentWrapper>
    </AccountCardSurface>
  );
};

export const AccountCard = React.memo(AccountCardComponent);
